import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, SparklesIcon, CodeBracketIcon, PaintBrushIcon, ArrowDownTrayIcon, CheckIcon } from '@heroicons/react/24/outline';

const Hero = () => {
  const [currentRole, setCurrentRole] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const roles = [
    { text: 'Full-Stack Developer', icon: CodeBracketIcon, color: 'from-blue-500 to-cyan-500' },
    { text: 'UI/UX Designer', icon: PaintBrushIcon, color: 'from-purple-500 to-pink-500' },
    { text: 'Problem Solver', icon: SparklesIcon, color: 'from-green-500 to-emerald-500' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRole((prev) => (prev + 1) % roles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const scrollToAbout = () => {
    document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
  };

  const downloadResume = () => {
    setIsDownloading(true);
    setTimeout(() => {
      setIsDownloading(false);
      // Add your actual resume download logic here
    }, 2000);
  };

  const floatingElements = Array.from({ length: 6 }, (_, i) => ({
    id: i,
    size: Math.random() * 100 + 50,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 20 + 10
  }));

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-900 dark:to-gray-800" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,#fff,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0.5))]" />
      
      {/* Floating Elements */}
      {floatingElements.map((element) => (
        <motion.div
          key={element.id}
          className="absolute rounded-full bg-gradient-to-r from-blue-400/20 to-purple-400/20 blur-xl"
          style={{
            width: element.size,
            height: element.size,
            left: `${element.x}%`,
            top: `${element.y}%`,
          }}
          animate={{
            x: [0, 30, -30, 0],
            y: [0, -30, 30, 0],
            scale: [1, 1.2, 0.8, 1],
          }}
          transition={{
            duration: element.duration,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
      
      {/* Main Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Greeting */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-white/90 to-blue-50/90 dark:from-gray-800/90 dark:to-gray-700/90 backdrop-blur-md border border-white/30 dark:border-gray-600/30 mb-8 shadow-2xl hover:shadow-blue-500/25 transition-all duration-300"
          >
            <motion.div
              animate={{ rotate: [0, 14, -8, 14, -4, 10, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 3 }}
              className="mr-3 text-3xl filter drop-shadow-lg"
            >
              👋
            </motion.div>
            <span className="text-gray-700 dark:text-gray-200 font-semibold text-lg">Hello, I'm</span>
          </motion.div>

          {/* Name */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-7xl md:text-9xl font-black mb-8 leading-tight tracking-tight"
          >
            <motion.span 
              className="bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent filter drop-shadow-2xl"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              Md
            </motion.span>
            <br />
            <motion.span 
              className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent filter drop-shadow-2xl"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              Kaif
            </motion.span>
          </motion.h1>
          
          {/* Dynamic Role */}
          <div className="h-20 mb-8 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentRole}
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.8 }}
                transition={{ duration: 0.5 }}
                className="flex items-center gap-3"
              >
                <motion.div
                  className={`p-3 rounded-xl bg-gradient-to-r ${roles[currentRole].color} shadow-lg`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  {React.createElement(roles[currentRole].icon, {
                    className: "w-6 h-6 text-white"
                  })}
                </motion.div>
                <span className="text-2xl md:text-3xl font-semibold text-gray-700 dark:text-gray-300">
                  {roles[currentRole].text}
                </span>
              </motion.div>
            </AnimatePresence>
          </div>
          
          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-16 leading-relaxed max-w-4xl mx-auto font-medium"
          >
            <span className="bg-gradient-to-r from-gray-700 to-gray-600 dark:from-gray-200 dark:to-gray-300 bg-clip-text text-transparent">
              Crafting exceptional digital experiences with modern technologies.
            </span>
            <br className="hidden md:block" />
            <span className="text-blue-600 dark:text-blue-400 font-semibold">
              Let's build something amazing together.
            </span>
          </motion.p>
          
          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-32"
          >
            <motion.button
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span>Let's Work Together</span>
              <span className="text-sm">→</span>
            </motion.button>
            

            
            <motion.button
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToAbout}
              className="px-8 py-4 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-xl font-semibold text-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-300 flex items-center gap-2"
            >
              <span>View My Work</span>
              <span className="text-sm">↓</span>
            </motion.button>
          </motion.div>


        </motion.div>
      </div>
      
      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <motion.button
          onClick={scrollToAbout}
          className="group flex flex-col items-center gap-3"
          whileHover={{ y: -2 }}
        >
          <span className="text-sm text-gray-500 dark:text-gray-400 font-semibold group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all duration-300 tracking-wide">
            Discover More
          </span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            className="p-4 rounded-full bg-gradient-to-r from-white/90 to-blue-50/90 dark:from-gray-800/90 dark:to-gray-700/90 backdrop-blur-md shadow-xl group-hover:shadow-2xl group-hover:shadow-blue-500/25 transition-all duration-300 border border-white/30 dark:border-gray-600/30"
          >
            <ChevronDownIcon className="w-6 h-6 text-gray-600 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300" />
          </motion.div>
        </motion.button>
      </motion.div>
    </section>
  );
};

export default Hero;